import { action, observable } from 'mobx'
import { ProductDTO } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { RootStore } from 'store/RootStore'
import { Api, applyProductQuery, parseQueryParams } from 'utilities'
import { ProductControllerQueryParams } from './ProductsMainStore.type'

export const PRODUCTS_MAIN_STORE = 'PRODUCTS_MAIN_STORE'

export class ProductsMainStore {
  private rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable private productsMap: Map<number, ProductDTO> = new Map()

  @action private setProduct = (product: ProductDTO) => {
    this.productsMap.set(product.id, product)
  }

  getProducts = async (
    queryParams: ProductControllerQueryParams = {}
  ): Promise<ProductDTO[]> => {
    let products: ProductDTO[] = []
    let filteredProductIds: number[] = []

    if (queryParams.ids) {
      filteredProductIds = queryParams.ids
        .filter(id => {
          const product = this.getProductById(id)
          const shouldBeLoadedFromApi = !(
            product &&
            (product.loadContents || !queryParams.loadContents) &&
            (product.loadProjects || !queryParams.loadProjects)
          )

          !shouldBeLoadedFromApi && products.push(product)
          return shouldBeLoadedFromApi
        })
    }

    if (queryParams.ids && filteredProductIds.length) {
      const {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      } = this.rootStore[GENERAL_STORE]
      setLoadingOn()
      await Api
        .get<ProductDTO[]>(`/product/all?${parseQueryParams({
          ...queryParams,
          ids: filteredProductIds
        })}`)
        .then(data => {
          const newlyLoadedProducts = data.map(p => {
            const product = applyProductQuery(p, queryParams)
            this.setProduct(product)
            return product
          })
          products = [
            ...products,
            ...newlyLoadedProducts
          ]
          setLoadingOff()
        })
        .catch(handleApiError)
    }

    return products
  }

  getProduct = async (
    productId: number,
    queryParams: ProductControllerQueryParams = {}
  ): Promise<ProductDTO> => {
    let product = this.getProductById(productId)

    const shouldLoadProductFromApi = !(
      product &&
      (product.loadContents || !queryParams.loadContents) &&
      (product.loadProjects || !queryParams.loadProjects)
    )

    if (shouldLoadProductFromApi) {
      await Api
        .get<ProductDTO>(
          `/product/${productId}/view?${parseQueryParams(queryParams)}`
        )
        .then(data => {
          product = applyProductQuery(data, queryParams)
          this.setProduct(product)
        })
        .catch(this.rootStore[GENERAL_STORE].handleApiError)
    }

    return product
  }

  getProductById = (productId: number): ProductDTO => {
    return this.productsMap.get(productId)
  }

  getProductBrandId = (productId: number): number => {
    return this.getProductById(productId)?.brandId
  }
}
